import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, RefreshCcw, RotateCcw, ChevronDown, ChevronRight } from "lucide-react";

const ACTION_COLORS = {
  create: "bg-emerald-100 text-emerald-800",
  update: "bg-amber-100 text-amber-800",
  delete: "bg-red-100 text-red-800",
};

const AuditLog = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [filters, setFilters] = useState({ entity: "", action: "", user_id: "", date_debut: "", date_fin: "" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.entity)     params.entity = filters.entity;
      if (filters.action)     params.action = filters.action;
      if (filters.user_id)    params.user_id = filters.user_id;
      if (filters.date_debut) params.date_debut = filters.date_debut;
      if (filters.date_fin)   params.date_fin = filters.date_fin;
      params.limit = 500;
      const res = await axios.get(`${API}/audit-log`, { headers, params });
      setItems(res.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Accès refusé");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const restore = async (audit_id, label) => {
    const reason = window.prompt(`Restaurer "${label}" à sa version précédente ?\n\nMotif (optionnel) :`, "");
    if (reason === null) return;
    try {
      await axios.post(`${API}/audit-log/restore`, { audit_id, reason }, { headers });
      toast.success("Version précédente restaurée ✓");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur restauration");
    }
  };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-indigo-600" /> Journal d&apos;audit
          </h1>
          <p className="text-slate-500 mt-1">
            Traçabilité complète des modifications : qui, quoi, quand. Restauration possible depuis un snapshot.
          </p>
        </div>
        <Button onClick={load} disabled={loading} data-testid="audit-refresh-btn">
          <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />Actualiser
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-sm">Filtres</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Select value={filters.entity || "all"} onValueChange={(v) => setFilters({ ...filters, entity: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="audit-filter-entity"><SelectValue placeholder="Type d'entité" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes entités</SelectItem>
              <SelectItem value="livraison">Livraisons</SelectItem>
              <SelectItem value="vente">Ventes</SelectItem>
              <SelectItem value="facture">Factures</SelectItem>
              <SelectItem value="client">Clients</SelectItem>
              <SelectItem value="produit">Produits</SelectItem>
              <SelectItem value="tournee">Tournées</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.action || "all"} onValueChange={(v) => setFilters({ ...filters, action: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="audit-filter-action"><SelectValue placeholder="Action" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes actions</SelectItem>
              <SelectItem value="create">Création</SelectItem>
              <SelectItem value="update">Modification</SelectItem>
              <SelectItem value="delete">Suppression</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" value={filters.date_debut} onChange={(e) => setFilters({ ...filters, date_debut: e.target.value })} data-testid="audit-filter-date-debut" />
          <Input type="date" value={filters.date_fin} onChange={(e) => setFilters({ ...filters, date_fin: e.target.value })} data-testid="audit-filter-date-fin" />
          <Button onClick={load} className="bg-indigo-600 hover:bg-indigo-700" data-testid="audit-apply-btn">Appliquer</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm">Événements ({items.length})</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="text-slate-400 text-sm py-8 text-center">Aucun événement dans le journal pour ces filtres.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Utilisateur</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Entité</TableHead>
                  <TableHead>Détail</TableHead>
                  <TableHead className="text-right">Restaurer</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((ev) => (
                  <>
                    <TableRow key={ev.id} data-testid={`audit-row-${ev.id}`}>
                      <TableCell>
                        <button type="button" onClick={() => toggleExpand(ev.id)} className="text-slate-400 hover:text-slate-700" data-testid={`audit-expand-${ev.id}`}>
                          {expanded.has(ev.id) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        {ev.ts ? new Date(ev.ts).toLocaleString("fr-FR") : "—"}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="font-medium">{ev.user_name || ev.user_email || "—"}</div>
                        <div className="text-slate-400">{ev.user_role}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${ACTION_COLORS[ev.action] || "bg-slate-100 text-slate-700"} border-0`}>
                          {ev.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="font-medium capitalize">{ev.entity}</div>
                        <div className="text-slate-400 font-mono">{ev.entity_id?.slice(0, 8)}…</div>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600 max-w-[280px] truncate">{ev.entity_label || "—"}</TableCell>
                      <TableCell className="text-right">
                        {ev.before && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => restore(ev.id, ev.entity_label || ev.entity_id)} data-testid={`audit-restore-${ev.id}`}>
                            <RotateCcw className="w-3 h-3 mr-1" />Restaurer
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    {expanded.has(ev.id) && (
                      <TableRow>
                        <TableCell colSpan={7} className="bg-slate-50">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
                            {ev.diff && Object.keys(ev.diff).length > 0 && (
                              <div className="md:col-span-2">
                                <p className="text-[11px] uppercase font-bold text-slate-600 mb-1">Champs modifiés</p>
                                <div className="space-y-1">
                                  {Object.entries(ev.diff).map(([k, v]) => (
                                    <div key={k} className="text-xs bg-white border rounded px-2 py-1">
                                      <span className="font-mono text-slate-500">{k} :</span>{" "}
                                      <span className="text-red-600 line-through">{JSON.stringify(v.before)}</span>{" → "}
                                      <span className="text-emerald-700 font-medium">{JSON.stringify(v.after)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {ev.before && (
                              <div>
                                <p className="text-[11px] uppercase font-bold text-slate-600 mb-1">Avant</p>
                                <pre className="text-[10px] bg-white border rounded p-2 max-h-64 overflow-auto">{JSON.stringify(ev.before, null, 2)}</pre>
                              </div>
                            )}
                            {ev.after && (
                              <div>
                                <p className="text-[11px] uppercase font-bold text-slate-600 mb-1">Après</p>
                                <pre className="text-[10px] bg-white border rounded p-2 max-h-64 overflow-auto">{JSON.stringify(ev.after, null, 2)}</pre>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLog;
